import * as dialog from "@zag-js/dialog";
import { normalizeProps, useMachine } from "@zag-js/solid";
import { create } from "domain";
import {
	DataProvider,
	EventBusProvider,
	Icon,
	Node,
	spec,
	specPatch,
	specTrack,
	specUpdate,
	ThemeProvider,
	useEventBus,
	useTheme,
} from "framework";
import {
	createEffect,
	createMemo,
	createSignal,
	createUniqueId,
	For,
	on,
	onCleanup,
	onMount,
	Show,
	splitProps,
} from "solid-js";
import { unwrap } from "solid-js/store";
import { Portal } from "solid-js/web";
import { compareObjects } from "utils";
import classes from "./renderer.module.css";

const HeaderBar = (props) => {
	const theme = useTheme();
	return (
		<div class={classes.topbar}>
			<Show when={props.logo}>
				<img src={props.logo} class={classes.logo} />
			</Show>
			<Show when={props.title}>
				<h1 class={classes.title}>{props.title}</h1>
			</Show>
		</div>
	);
};

const testEvent = (event, trigger) => {
	if (!trigger) return false;
	if (!event) return false;
	return compareObjects(event, unwrap(trigger));
};

const PanelNode = (props) => {
	const theme = useTheme();
	const { triggerEvent } = useEventBus();
	const service = useMachine(dialog.machine, {
		id: createUniqueId(),
		modal: true,
		defaultOpen: false,
		onOpenChange(details) {
			if (details.open) props.onOpen();
			else props.onClose();

			triggerEvent({
				action: details.open ? "open" : "close",
				source: `/panels/${props.id}`,
				data: null,
			});
		},
	});
	const api = createMemo(() => dialog.connect(service, normalizeProps));
	createEffect(
		on(
			() => props.open,
			() => {
				if (props.open !== api().open) api().setOpen(props.open);
			},
		),
	);

	const align =
		props.position?.top || props.position?.bottom ? "start" : "center";
	const justify =
		props.position?.left || props.position?.right ? "start" : "center";

	return (
		<Show when={api().open}>
			<Portal mount={document.querySelector("#eidos")}>
				<div {...api().getBackdropProps()} />
				<div
					{...api().getPositionerProps()}
					style={{
						"align-items": align,
						"justify-content": justify,
					}}
				>
					<div
						style={{
							height: props.height,
							width: props.width,
							"z-index": 100,
							...props.position,
						}}
						{...api().getContentProps()}
					>
						<Show when={props.title}>
							<h2 style={{ padding: "10px" }} {...api().getTitleProps()}>
								{props.title}
							</h2>
						</Show>
						<button
							{...api().getCloseTriggerProps()}
							class={classes.closeButton}
						>
							<Icon url="icon://x-circle" size={24} color={theme().textColor} />
						</button>
						<Node
							id={props.node.id}
							nodeSpec={props.node}
							nodeType={props.node.nodeType}
							path={`/panels/#${props.id}/node`}
						/>
					</div>
				</div>
			</Portal>
		</Show>
	);
};

export const Eidos = (props) => {
	const { lastEvent, triggerEvent } = useEventBus();
	const [panelOpen, setPanelOpen] = createSignal([]);

	const openPanel = (panel) => {
		if (panel.exclusive) {
			setPanelOpen([panel.id]);
		} else {
			setPanelOpen((prev) =>
				prev.filter((m) => m !== panel.id).concat(panel.id),
			);
		}
	};

	const closePanel = (panelid) => {
		setPanelOpen((prev) => prev.filter((m) => m !== panelid));
		const panelIndex = spec.panels.findIndex((p) => p.id === panelid);
		specPatch([
			{
				op: "replace",
				path: `/panels/${panelIndex}/open`,
				value: false,
			},
		]);
	};

	onMount(() => {
		triggerEvent({
			action: "status",
			source: "eidos",
			data: { code: 1, message: "ready" },
		});
	});

	createEffect(
		on(lastEvent, () => {
			//console.log('Event triggered');
			if (spec.panels && spec.panels.length > 0) {
				spec.panels.forEach((panel) => {
					if (lastEvent() && testEvent(lastEvent(), panel.trigger)) {
						openPanel(panel);
					}
				});
			}
		}),
	);

	createEffect(
		on(
			() => specTrack(spec.panels),
			() => {
				if (spec.panels && spec.panels.length > 0) {
					spec.panels.forEach((panel) => {
						if (panel.open) {
							openPanel(panel);
						}
					});
				}
			},
		),
	);

	return (
		<ThemeProvider theme={spec.theme || {}}>
			<For each={spec.panels}>
				{(panel, i) => (
					<PanelNode
						id={panel.id}
						node={panel.node}
						title={panel.title}
						width={panel.width}
						height={panel.height}
						position={panel.position}
						onClose={() => closePanel(panel.id)}
						onOpen={() => openPanel(panel.id)}
						open={panelOpen().includes(panel.id)}
					/>
				)}
			</For>
			<Show when={spec.title || spec.logo}>
				<HeaderBar title={spec.title} logo={spec.logo} />
			</Show>
			<Show when={spec.root.id} keyed>
				<div
					style={{
						width: "100%",
						height: `calc( 100% - ${spec.title || spec.logo ? 50 : 0}px )`,
						display: "flex",
					}}
				>
					<Node
						id={spec.root.id}
						path="/root"
						nodeSpec={spec.root}
						nodeType={spec.root.nodeType}
					/>
				</div>
			</Show>
		</ThemeProvider>
	);
};

export const Renderer = ({ specOrigins, id }) => {
	const [auth, setAuth] = createSignal(null);
	let wrapperRef;
	const handleMessage = (ev) => {
		console.log("received message", ev.data);
		if (!ev.data || !ev.data.type || !ev.data.id || ev.data.id != id) {
			console.log("ignoring message", ev.data);
			return;
		}
		try {
			if (ev.data.type === "spec") {
				//console.log('received spec', ev.data.payload);
				specUpdate(ev.data.payload);
			} else if (ev.data.type === "patch") {
				//console.log('received patch', ev.data.payload);
				specPatch(ev.data.payload);
			} else if (ev.data.type === "auth") {
				//console.log('received auth', ev.data.payload);
				setAuth(ev.data.payload);
			} else if (ev.data.type === "event") {
				console.log("received event", ev.data.payload);
				if (ev.data.payload.type === "dragstart") {
					console.log("dragstart");
					const drag = new DragEvent("dragstart");
					wrapperRef.current.dispatchEvent(drag);
				}
			}
		} catch (ex) {
			console.error(ex);
		}
	};
	window.addEventListener("message", handleMessage);

	onCleanup(() => {
		window.removeEventListener("message", handleMessage);
	});

	return (
		<DataProvider
			authToken={auth}
			datameshService={DATAMESH_SERVICE}
			gatewayService={GATEWAY_SERVICE}
		>
			<EventBusProvider>
				<div
					className={classes.wrapper}
					ref={wrapperRef}
					//onContextMenu={(e) => e.preventDefault()}
				>
					<Eidos id={id} />
				</div>
			</EventBusProvider>
		</DataProvider>
	);
};
